import React from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PricingPreview = () => {
  const pricingTiers = [
    {
      id: 'launch',
      name: 'Launch',
      price: '$9',
      period: '/month',
      description: 'Perfect for individuals getting started',
      features: [
        'Basic prompt generation',
        'Up to 100 prompts/month',
        'Email support',
        'Standard templates'
      ],
      popular: false,
      color: 'var(--color-secondary)'
    },
    {
      id: 'scale-up',
      name: 'Scale-Up',
      price: '$29',
      period: '/month',
      description: 'Ideal for growing teams and businesses',
      features: [
        'Categorized prompts',
        'Save & organize prompts',
        'Up to 1,000 prompts/month',
        'Priority support',
        'Advanced templates',
        'Team collaboration'
      ],
      popular: true,
      color: 'var(--color-primary)'
    },
    {
      id: 'command',
      name: 'Command',
      price: '$99',
      period: '/month',
      description: 'Enterprise solution for power users',
      features: [
        'Custom GPT system prompts',
        'Master workflow builder',
        'Unlimited prompts',
        'Dedicated support',
        'API access',
        'Custom integrations',
        'Advanced analytics'
      ],
      popular: false,
      color: 'var(--color-accent)'
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-gradient-to-br from-muted/30 to-background">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center space-y-6 mb-16">
          <h2 className="text-3xl md:text-5xl font-bold text-text-primary">
            Choose Your
            <span className="block text-primary">Perfect Plan</span>
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Start free and scale as you grow. Every plan includes our core AI-powered prompt generation.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {pricingTiers?.map((tier) => (
            <div
              key={tier?.id}
              className={`relative bg-card border rounded-2xl p-8 hover:shadow-modal transition-state hover-scale ${
                tier?.popular 
                  ? 'border-primary shadow-lg ring-2 ring-primary/20' 
                  : 'border-border'
              }`}
            >
              {tier?.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-primary text-primary-foreground px-4 py-2 rounded-full text-sm font-medium">
                    Most Popular
                  </div>
                </div>
              )}

              <div className="space-y-6">
                {/* Header */}
                <div className="text-center space-y-2">
                  <h3 className="text-2xl font-bold text-text-primary">{tier?.name}</h3>
                  <p className="text-text-secondary">{tier?.description}</p>
                </div>

                {/* Price */}
                <div className="text-center space-y-1">
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-bold text-text-primary">{tier?.price}</span>
                    <span className="text-text-secondary ml-1">{tier?.period}</span>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-4">
                  <ul className="space-y-3">
                    {tier?.features?.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-success/10 flex items-center justify-center mt-0.5">
                          <Icon name="Check" size={12} color="var(--color-success)" />
                        </div>
                        <span className="text-text-secondary text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                <div className="pt-4">
                  <Link to="/pricing-plans" className="block">
                    <Button
                      variant={tier?.popular ? "default" : "outline"}
                      size="lg"
                      fullWidth
                      iconName="ArrowRight"
                      iconPosition="right"
                    >
                      Choose {tier?.name}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16 space-y-4">
          <p className="text-text-secondary">
            Need a custom solution? We've got you covered.
          </p>
          <Link to="/pricing-plans">
            <Button variant="ghost" iconName="MessageCircle" iconPosition="left">
              Contact Sales
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PricingPreview;